import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { DataStats } from "@/components/data-stats/DataStats";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { listLanguages, cleanLanguages, guessLanguagesBulk, PaginatedLanguagesResponse, CleanLanguagesResponse, GuessLanguagesResponse } from "@/services/languagesApi";
import { Button } from "@/components/ui/button";
import { ArrowUpDown, ArrowUp, ArrowDown, Sparkles, Loader2, Wand2 } from "lucide-react";
import { confirmDialog } from "@/components/ui/confirm-dialog";

type SortBy = "name" | "iso_code" | "candidate_count";

function SortHeader<T extends string>({
  label,
  field,
  current,
  order,
  onSort,
}: {
  label: string;
  field: T;
  current: T;
  order: string;
  onSort: (field: T) => void;
}) {
  const active = current === field;
  return (
    <th
      className="px-3 py-2 text-left cursor-pointer select-none hover:bg-gray-100"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {active ? (
          order === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
        )}
      </div>
    </th>
  );
}

export default function LanguagesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PaginatedLanguagesResponse | null>(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [sortBy, setSortBy] = useState<SortBy>("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [resolvedOnly, setResolvedOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cleaning, setCleaning] = useState(false);
  const [cleanResult, setCleanResult] = useState<CleanLanguagesResponse | null>(null);
  const [guessing, setGuessing] = useState(false);
  const [guessResult, setGuessResult] = useState<GuessLanguagesResponse | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listLanguages(navigate, { page, size, sort_by: sortBy, sort_order: sortOrder, resolved_only: resolvedOnly });
        setData(res);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur inconnue");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, page, size, sortBy, sortOrder, resolvedOnly]);

  const handleSort = (field: SortBy) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder(field === "name" ? "asc" : "desc");
    }
    setPage(1);
  };

  const handleClean = async () => {
    const ok = await confirmDialog({
      title: "Nettoyer les langues",
      description:
        "Cette action va tenter de resoudre les noms en ISO 639 et fusionner les doublons.",
      confirmLabel: "Nettoyer",
    });
    if (!ok) return;
    setCleaning(true);
    setCleanResult(null);
    try {
      const result = await cleanLanguages(navigate);
      setCleanResult(result);
      // Refresh the list
      const res = await listLanguages(navigate, { page, size, sort_by: sortBy, sort_order: sortOrder, resolved_only: resolvedOnly });
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors du nettoyage");
    } finally {
      setCleaning(false);
    }
  };

  const handleGuess = async () => {
    const ok = await confirmDialog({
      title: "Deviner les langues",
      description:
        "Inferer les langues des candidats sans langues declarees a partir du texte du profil (intitule, description, experiences), avec fallback sur la localisation.",
      confirmLabel: "Deviner",
    });
    if (!ok) return;
    setGuessing(true);
    setGuessResult(null);
    try {
      const result = await guessLanguagesBulk(navigate);
      setGuessResult(result);
      // Refresh the list
      const res = await listLanguages(navigate, { page, size, sort_by: sortBy, sort_order: sortOrder, resolved_only: resolvedOnly });
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors de la devinette");
    } finally {
      setGuessing(false);
    }
  };

  const canPrev = page > 1;
  const canNext = data ? page < data.pages : false;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Langues</h1>
          </div>
        </header>

        <div className="p-6 space-y-4">
          <DataStats entity="languages" />
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Liste des langues</h2>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleClean} disabled={cleaning}>
                {cleaning ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                Nettoyer les langues
              </Button>
              <Button variant="outline" size="sm" onClick={handleGuess} disabled={guessing}>
                {guessing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wand2 className="h-4 w-4 mr-1" />}
                Deviner les langues manquantes
              </Button>
              <label className="flex items-center gap-1 text-sm text-gray-600 select-none">
                <input
                  type="checkbox"
                  checked={resolvedOnly}
                  onChange={(e) => { setPage(1); setResolvedOnly(e.target.checked); }}
                />
                Resolues uniquement
              </label>
              <label className="text-sm text-gray-600">Taille page</label>
              <select
                className="border rounded px-2 py-1 text-sm"
                value={size}
                onChange={(e) => { setPage(1); setSize(parseInt(e.target.value, 10)); }}
              >
                {[10, 25, 50, 100].map((n) => (<option key={n} value={n}>{n}</option>))}
              </select>
            </div>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}
          {cleanResult && (
            <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
              <strong>Nettoyage termine :</strong> {cleanResult.resolved} resolue(s), {cleanResult.merged} fusionnee(s), {cleanResult.split} eclatee(s), {cleanResult.deduped} dedoublonnee(s), {cleanResult.still_unresolved} non resolue(s)
            </div>
          )}
          {guessResult && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
              <strong>Devinette terminee :</strong> {guessResult.candidates_updated} candidat(s) mis a jour sur {guessResult.total_without_languages} sans langues
            </div>
          )}
          {loading && <div className="text-gray-500 text-sm">Chargement...</div>}

          <div className="overflow-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Id</th>
                  <SortHeader label="Nom" field="name" current={sortBy} order={sortOrder} onSort={handleSort} />
                  <SortHeader label="ISO 639-3" field="iso_code" current={sortBy} order={sortOrder} onSort={handleSort} />
                  <th className="px-3 py-2 text-left">ISO 639-1</th>
                  <SortHeader label="Candidats" field="candidate_count" current={sortBy} order={sortOrder} onSort={handleSort} />
                </tr>
              </thead>
              <tbody>
                {data?.items?.map((s) => (
                  <tr key={s.id} className="border-t hover:bg-muted/50">
                    <td className="px-3 py-2">{s.id}</td>
                    <td className="px-3 py-2 font-medium">
                      {s.name}
                    </td>
                    <td className="px-3 py-2 font-mono">{s.iso_code ?? "—"}</td>
                    <td className="px-3 py-2 font-mono">{s.iso_639_1 ?? "—"}</td>
                    <td className="px-3 py-2">{s.candidate_count}</td>
                  </tr>
                ))}
                {!data?.items?.length && !loading && (
                  <tr><td className="px-3 py-4 text-center text-gray-500" colSpan={5}>Aucune langue</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {data?.page ?? page} / {data?.pages ?? 1} — {data?.total ?? 0} elements
            </div>
            <div className="flex gap-2">
              <Button variant="outline" disabled={!canPrev} onClick={() => setPage((p) => Math.max(1, p - 1))}>Precedent</Button>
              <Button variant="outline" disabled={!canNext} onClick={() => setPage((p) => p + 1)}>Suivant</Button>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
